import {
  afterNextRender,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Requerimento,
  Resumo,
  STATUS_CANCELAVEL,
  STATUS_ENVIADA,
  StatusRequerimento,
  Termo,
} from '../../../core/models/requerimento';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import { formatarAliquota, formatarValor } from '../../../shared/utils/formatadores';
import { AntecipacaoResumo, formaDeRecebimento } from '../../../shared/utils/beneficio-resumo';
import { ROTA_PAGINA_INDISPONIVEL, rotaDoErroRequerimento } from '../../../shared/utils/requerimento-erros';
import { centralizarModalNaAreaVisivel, VivestModalElement } from '../../../shared/utils/modal-iframe';

/** Status da solicitação que muda o card exibido. */
export const STATUS_DOCUMENTOS_EM_ANALISE = 'Documentos em análise';
export const STATUS_DOCUMENTACAO_NAO_VALIDADA = 'Documentação não validada';
export const STATUS_DOCUMENTACAO_VALIDADA = 'Documentação validada';
export const STATUS_EM_PROCESSAMENTO = 'Em processamento';
export const STATUS_ERRO_PROCESSAMENTO = 'Erro ao processar requisição';

/** Texto do card por tp_status; A e E (documento reenviado) seguem em análise. */
const TEXTO_STATUS: Partial<Record<StatusRequerimento, string>> = {
  A: STATUS_DOCUMENTOS_EM_ANALISE,
  P: STATUS_DOCUMENTACAO_NAO_VALIDADA,
  E: STATUS_DOCUMENTOS_EM_ANALISE,
  V: STATUS_DOCUMENTACAO_VALIDADA,
  L: STATUS_EM_PROCESSAMENTO,
  F: STATUS_ERRO_PROCESSAMENTO,
};

// TODO: a API ainda não devolve a data do primeiro benefício; substituir quando houver a fonte.
export const PRIMEIRO_BENEFICIO_MOCK = '9999-10-14T00:00:00';

// TODO: a API ainda não devolve a previsão da análise dos documentos (dias_uteis_previsao); substituir quando houver.
export const DIAS_UTEIS_PREVISAO_MOCK: number = 3;

// TODO: o resumo só devolve o percentual da antecipação (pct_antecip_saldo); bruto, IR e líquido ficam mockados até o
// backend devolver esses valores. A tela só exibe, sem calcular.
export const ANTECIPACAO_MOCK = { valorBruto: 999.99, valorImpostoRenda: 999.99, valorLiquido: 999.99 };

const MENSAGEM_ERRO_CANCELAR = 'Não foi possível cancelar a solicitação. Tente novamente.';
const MENSAGEM_ERRO_TERMO = 'Não foi possível carregar o termo assinado. Tente novamente.';

@Component({
  imports: [PdfDocument],
  selector: 'app-detalhes-requerimento',
  styleUrl: './detalhes-requerimento.scss',
  templateUrl: './detalhes-requerimento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DetalhesRequerimento implements OnInit {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);
  private readonly modalCancelar = viewChild<ElementRef<VivestModalElement>>('modalCancelar');
  private readonly modalDocumentacao = viewChild<ElementRef<VivestModalElement>>('modalDocumentacao');

  readonly requerimento = signal<Requerimento | null>(null);
  /** Snapshot da etapa 6, congelado na assinatura do termo: são os valores do benefício exibidos. */
  readonly resumo = signal<Resumo | null>(null);
  /** Termo assinado: de onde vem a data da assinatura. */
  readonly termo = signal<Termo | null>(null);
  /** Modal "Deseja cancelar sua Solicitação?". */
  readonly openModal = signal(false);
  /** Modal "Não conseguimos validar a sua documentação". */
  readonly openDocModal = signal(false);
  /** Endereço local (blob:) do termo assinado; enquanto vazio, o visualizador não é renderizado. */
  readonly pdfSrc = signal<string | undefined>(undefined);

  protected readonly statusDocumentacaoNaoValidada = STATUS_DOCUMENTACAO_NAO_VALIDADA;
  /** Previsão exibida enquanto os documentos estão em análise. */
  protected readonly diasUteisPrevisao = DIAS_UTEIS_PREVISAO_MOCK;

  /** Pendente de documentação (P): algum documento foi recusado na análise e precisa ser reenviado. */
  readonly statusSolicitacao = computed(() => {
    const status = this.requerimento()?.status;
    return (status && TEXTO_STATUS[status]) || STATUS_DOCUMENTOS_EM_ANALISE;
  });

  /** A previsão só vale enquanto os documentos estão em análise. */
  readonly emAnalise = computed(() => this.statusSolicitacao() === STATUS_DOCUMENTOS_EM_ANALISE);

  /** Validado o documento (V em diante), o participante não cancela mais; até a solicitação chegar, o botão aparece. */
  readonly podeCancelar = computed(() => {
    const status = this.requerimento()?.status;
    return !status || STATUS_CANCELAVEL.includes(status);
  });

  /** Saldo geral: conta de contribuição do participante mais a da patrocinadora. */
  readonly saldoGeral = computed(() => {
    const resumo = this.resumo();
    return resumo ? resumo.saldoParticipante + resumo.saldoPatrocinadora : 0;
  });

  /**
   * Bloco "Antecipação do benefício": exibido só com antecipação (pct_antecip_saldo > 0). O percentual vem do resumo;
   * os valores ainda são mockados.
   */
  readonly antecipacao = computed<AntecipacaoResumo | null>(() => {
    const percentual = this.resumo()?.percentualAntecipacao;
    return percentual && percentual > 0 ? { percentual, ...ANTECIPACAO_MOCK } : null;
  });

  /** Etapa 2 do requerimento: modalidade com o seu valor, ex.: "Prazo Certo (5 anos)". */
  readonly formaRecebimento = computed(() => formaDeRecebimento(this.requerimento()?.beneficio));

  readonly adiantamentoBeneficio = computed(() => {
    const adianta = this.requerimento()?.beneficio?.adiantaBeneficio;
    if (adianta === undefined) return '-';
    return adianta ? 'Sim' : 'Não';
  });

  /** Texto ao lado do ícone de check no visualizador; vazio enquanto não há a data da assinatura. */
  readonly textoAssinatura = computed(() => {
    const dataAssinatura = this.termo()?.dataAssinatura;
    if (!dataAssinatura) return '';
    return `Assinado com sucesso em ${formatDate(dataAssinatura, "dd/MM/yyyy 'às' HH:mm", 'en-US')}`;
  });

  readonly primeiroBeneficio = computed(() => formatDate(PRIMEIRO_BENEFICIO_MOCK, 'dd/MM/yyyy', 'en-US'));

  protected readonly formatarValor = formatarValor;
  /** Percentuais e anos sem casas fixas (15 → "15"; 12.5 → "12,5"). */
  protected readonly formatarNumero = formatarAliquota;

  constructor() {
    // Libera da memória o PDF criado com URL.createObjectURL ao sair da tela.
    inject(DestroyRef).onDestroy(() => this.liberarPdf());

    // Os modais aparecem no meio da área que a pessoa está vendo, e não no meio da página inteira.
    this.centralizarAoAbrir(this.openModal, this.modalCancelar);
    this.centralizarAoAbrir(this.openDocModal, this.modalDocumentacao);
  }

  /** Enquanto o modal está aberto, mantém o diálogo centralizado na área visível do iframe. */
  private centralizarAoAbrir(aberto: Signal<boolean>, modal: Signal<ElementRef<VivestModalElement> | undefined>): void {
    effect((onCleanup) => {
      if (!aberto()) return;

      let parar = (): void => {};
      // Depois da renderização, quando o vivest-modal já recebeu o `open`.
      const render = afterNextRender(
        () => {
          const elemento = modal()?.nativeElement;
          if (elemento) parar = centralizarModalNaAreaVisivel(elemento);
        },
        { injector: this.injector },
      );

      onCleanup(() => {
        render.destroy();
        parar();
      });
    });
  }

  ngOnInit(): void {
    void this.carregarSolicitacao();
  }

  private async carregarSolicitacao(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const requerimentos = await this.requerimentoService.listar();
      const enviada = requerimentos.find((item) => STATUS_ENVIADA.includes(item.status));
      if (enviada) {
        this.requerimento.set(enviada);
        const [resumo] = await Promise.all([
          this.requerimentoService.getResumo(enviada.id),
          this.carregarTermo(enviada.id),
        ]);
        this.resumo.set(resumo);
      } else {
        this.router.navigate(['/requerimento-beneficio']);
      }
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  closeModal(): void {
    this.openModal.set(false);
  }

  /** "Reenviar documentos" do card: abre o modal, de onde a pessoa segue para o reenvio. */
  abrirDocModal(): void {
    this.openDocModal.set(true);
  }

  closeDocModal(): void {
    this.openDocModal.set(false);
  }

  onSubmit(): void {
    this.openModal.set(true);
  }

  async cancelar(): Promise<void> {
    this.closeModal();
    const requerimento = this.requerimento();
    if (!requerimento || !this.podeCancelar()) return;

    this.loaderService.showLoader('Cancelando...');
    try {
      await this.requerimentoService.cancelar(requerimento.id);
      this.requerimentoState.limpar();
      this.router.navigate(['/requerimento-beneficio']);
    } catch (erro) {
      if (erro instanceof HttpErrorResponse && erro.status === 401) {
        this.router.navigate(['/token-invalido']);
      } else {
        this.toastService.show(MENSAGEM_ERRO_CANCELAR, 'error');
      }
    } finally {
      this.loaderService.hideLoader();
    }
  }

  openDocPage(): void {
    this.closeDocModal();
    this.router.navigate(['/requerimento-beneficio/upload-documento']);
  }

  /** Sem o termo, os detalhes continuam na tela: só o visualizador fica vazio. */
  private async carregarTermo(id: number): Promise<void> {
    try {
      const [termo, pdf] = await Promise.all([
        this.requerimentoService.getTermo(id),
        this.requerimentoService.getArquivoTermo(id),
      ]);
      this.termo.set(termo);
      this.liberarPdf();
      this.pdfSrc.set(URL.createObjectURL(pdf));
    } catch (erro) {
      if (erro instanceof HttpErrorResponse && erro.status === 401) {
        this.router.navigate(['/token-invalido']);
      } else {
        this.toastService.show(MENSAGEM_ERRO_TERMO, 'error');
      }
    }
  }

  private liberarPdf(): void {
    const src = this.pdfSrc();
    if (src) URL.revokeObjectURL(src);
  }
}
